import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { adminApi, apiError, ORDER_STATUS, fmtDate } from "@/lib/api";
import { eur, eur2 } from "@/lib/product";
import { StatusBadge } from "./OrdersTab";
import { EmailLogList } from "./EmailBadge";
import { ShipDialog } from "./ShipDialog";
import { StripeBadge, StripeTimeline } from "./StripeBadge";
import { RefreshCw } from "lucide-react";
import { RefundDialog, RefundList, remainingAmount } from "./RefundDialog";
import { CapiBadge } from "./CapiBadge";

const REFUND_STATUSES = ["remboursee", "partiellement_remboursee"];

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-slate-100 text-sm">
    <span className="text-slate-500">{k}</span>
    <span className="text-right text-slate-900 font-medium break-all">{v}</span>
  </div>
);

export const OrderDetail = ({ orderId, onClose, onUpdated }) => {
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const [shipOpen, setShipOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);

  useEffect(() => {
    if (!orderId) return setOrder(null);
    adminApi.get(`/orders/${orderId}`).then(({ data }) => { setOrder(data); setStatus(data.status); }).catch((e) => toast.error(apiError(e)));
  }, [orderId]);

  const refresh = async () => {
    const { data } = await adminApi.get(`/orders/${orderId}`);
    setOrder(data);
  };

  const resend = async () => {
    setSaving(true);
    try {
      const { data } = await adminApi.post(`/orders/${orderId}/review-invite`);
      data.ok ? toast.success("Invitation envoyée") : toast.error(`Échec de l'envoi : ${data.error}`);
      await refresh();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const resendShipping = async () => {
    setSaving(true);
    try {
      const { data } = await adminApi.post(`/orders/${orderId}/shipping-email`);
      data.ok ? toast.success("E-mail d'expédition renvoyé") : toast.error(`Échec de l'envoi : ${data.error}`);
      await refresh();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const syncEmails = async () => {
    setSyncing(true);
    try {
      await adminApi.post("/emails/refresh", null, { params: { ref: `order:${orderId}` } });
      await refresh();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSyncing(false);
    }
  };

  const onShipped = (data) => {
    setOrder(data);
    setStatus(data.status);
    setShipOpen(false);
    onUpdated();
  };

  const save = async () => {
    if (status === "expediee" && order.status === "payee") return setShipOpen(true);
    setSaving(true);
    try {
      const { data } = await adminApi.patch(`/orders/${orderId}`, { status });
      setOrder(data);
      toast.success("Statut mis à jour");
      onUpdated();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!orderId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="order-detail-dialog" className="sm:max-w-xl max-h-[90vh] overflow-y-auto rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Commande {orderId}</DialogTitle>
          <DialogDescription>{order && <StatusBadge status={order.status} />}</DialogDescription>
        </DialogHeader>
        {order && (
          <div className="flex flex-col gap-5">
            <div>
              <Row k="Client" v={`${order.first_name} ${order.last_name}`} />
              <Row k="E-mail" v={order.email} />
              <Row k="Téléphone" v={order.phone} />
              <Row k="Adresse" v={`${order.address.street}, ${order.address.postal_code} ${order.address.city}`} />
              <Row k="Quantité" v={order.quantity} />
              <Row k="Prix unitaire" v={`${eur(order.unit_price)}${order.offer_price_applied ? " (prix de déstockage)" : ""}`} />
              <Row k="Livraison" v={eur(order.shipping_fee)} />
              {order.promo_code && <Row k="Code promo" v={<span data-testid="order-detail-promo">{order.promo_code} (−{eur2(order.discount)})</span>} />}
              <Row k="Relances panier" v={<span data-testid="order-detail-reminders">{order.reminders.length === 0 ? "Aucune" : order.reminders.map((r) => `#${r.n} le ${fmtDate(r.at)}${r.ok ? "" : " (échec)"}${r.code ? ` · code ${r.code}` : ""}`).join(" — ")}</span>} />
              <Row k="Total" v={<span data-testid="order-detail-total">{eur(order.total)}</span>} />
              <Row k="Montant encaissé Stripe" v={order.stripe_amount_total != null ? eur(order.stripe_amount_total) : "—"} />
              <Row k="Créée le" v={fmtDate(order.created_at)} />
              <Row k="Payée le" v={fmtDate(order.paid_at)} />
              <Row k="Session Stripe" v={<span className="font-mono text-xs">{order.stripe_session_id || "—"}</span>} />
            </div>
            {order.tracking_number && (
              <div data-testid="order-detail-shipping" className="rounded-xl bg-sky-50 border border-sky-100 p-4 text-sm">
                <span className="font-semibold text-slate-800">Expédition</span>
                <Row k="Transporteur" v={<span data-testid="order-detail-carrier">{order.carrier}</span>} />
                <Row k="N° de suivi" v={<span data-testid="order-detail-tracking-number" className="font-mono">{order.tracking_number}</span>} />
                <Row k="Lien de suivi" v={order.tracking_url ? <a data-testid="order-detail-tracking-link" href={order.tracking_url} target="_blank" rel="noreferrer" className="text-sky-700 underline">Ouvrir le suivi</a> : "—"} />
                <Row k="Expédiée le" v={fmtDate(order.shipped_at)} />
                <Row k="E-mail d'expédition" v={order.shipping_email_sent_at ? `envoyé le ${fmtDate(order.shipping_email_sent_at)}` : "non envoyé"} />
                <div className="flex flex-wrap gap-2 mt-3">
                  <button type="button" onClick={resendShipping} disabled={saving} data-testid="order-detail-resend-shipping-button" className="btn-ghost-ice !py-2 text-xs">
                    Renvoyer l'e-mail d'expédition
                  </button>
                  <button type="button" onClick={() => setShipOpen(true)} disabled={saving} data-testid="order-detail-edit-tracking-button" className="btn-ghost-ice !py-2 text-xs">
                    Modifier le suivi
                  </button>
                </div>
              </div>
            )}
            {order.refunds?.length > 0 && <RefundList order={order} />}
            {order.payment_status === "paid" && order.stripe_payment_intent && order.status !== "remboursee" && remainingAmount(order) > 0 && (
              <button type="button" onClick={() => setRefundOpen(true)} data-testid="order-detail-refund-button" className="btn-ghost-ice !py-2 text-sm self-start">
                Rembourser
              </button>
            )}
            <div data-testid="order-detail-stripe">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-800">Paiement Stripe</span>
                <StripeBadge order={order} testId="order-detail-stripe-badge" />
              </div>
              <StripeTimeline events={order.stripe_events || []} />
              <div className="mt-3"><CapiBadge order={order} /></div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-800">E-mails</span>
                <button type="button" onClick={syncEmails} disabled={syncing} data-testid="order-detail-refresh-emails-button" className="text-xs text-sky-700 hover:underline inline-flex items-center gap-1">
                  <RefreshCw className={`w-3 h-3 ${syncing ? "animate-spin" : ""}`} /> Actualiser les statuts
                </button>
              </div>
              <EmailLogList logs={order.email_log} testId="order-email-log" />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <select data-testid="order-detail-status-select" value={status} onChange={(e) => setStatus(e.target.value)} className="field-input max-w-[14rem]">
                {Object.entries(ORDER_STATUS).filter(([k]) => (k !== "en_attente_paiement" && !REFUND_STATUSES.includes(k)) || order.status === k).map(([k, [l]]) => (
                  <option key={k} value={k} disabled={k === "en_attente_paiement"}>{l}</option>
                ))}
              </select>
              <button type="button" onClick={save} disabled={saving || status === order.status} data-testid="order-detail-save-button" className="btn-heat !py-2.5 disabled:opacity-50">
                Enregistrer le statut
              </button>
              {order.status === "livree" && !order.review_submitted && (
                <button type="button" onClick={resend} disabled={saving} data-testid="order-detail-resend-review-button" className="btn-ghost-ice !py-2.5">
                  Renvoyer l'invitation
                </button>
              )}
            </div>
            <p data-testid="order-detail-review-info" className="text-xs text-slate-500">
              Avis : {order.review_submitted ? "déposé" : order.review_invite_sent_at ? `invitation envoyée le ${fmtDate(order.review_invite_sent_at)} (lien valable jusqu'au ${fmtDate(order.review_token_expires)})` : "aucune invitation"}
            </p>
          </div>
        )}
        {order && <ShipDialog order={order} open={shipOpen} onClose={() => setShipOpen(false)} onShipped={onShipped} />}
        {order && <RefundDialog order={order} open={refundOpen} onClose={() => setRefundOpen(false)} onDone={(d) => { setOrder(d); setStatus(d.status); setRefundOpen(false); onUpdated(); }} />}
      </DialogContent>
    </Dialog>
  );
};
